<!--
  Detalle de una solicitud de crédito — PÁGINA con ruta propia, a todo el ancho.
  Estilo: clases `.kb-*` de `src/styles.css`. Sin Bootstrap, sin colores crudos.

  🔴 CUATRO BLOQUES, SIN PESTAÑAS (documento 63 §4.2): Cabecera (sin rótulo) · Historial (pieza
  central, con el carril del cobro adentro) · Plan de pago · Crédito. La regla que lo ordena:
  lo que tiene fecha vive en la línea de tiempo; lo que no tiene fecha vive en la ficha.

  🔴 Rediseño 2026-09-20 — lo que cambió en esta vuelta, y por qué:
    · los tres bloques eran tres `.kb-card`. Una caja que sólo agrupa cosas que se leen no
      se gana el borde ni la sombra: son secciones planas (`.kb-seccion`), que es el molde.
    · fuera los subtítulos debajo de cada título. El de Historial se quedó, recortado a
      «De más viejo a más nuevo», porque dice algo que el título no dice.
    · la cabecera pinta el cajero, que ya llegaba en la respuesta y no se veía.
    · el `mt-12` entre bloques era letra muerta: `.kb-page` trae `space-y-6` y su selector
      pisa cualquier `mt-*` de los hijos. El aire sale de ahí, y no de un número que no se
      aplicaba.
-->
<div class="kb-page kb-gateway">

  <!-- ══ Migas ════════════════════════════════════════════════════════════════ -->
  <nav class="kb-migas" aria-label="Ubicación">
    <a class="kb-miga" [routerLink]="['/console/solicitudes-credito']">Solicitudes de crédito</a>
    <span class="kb-miga-sep" aria-hidden="true"><i class="fas fa-chevron-right"></i></span>
    <span class="kb-miga-actual">{{ codigo }}</span>
  </nav>

  <!-- ══ Cargando ═════════════════════════════════════════════════════════════ -->
  <div *ngIf="cargando" class="space-y-4">
    <div class="kb-skeleton h-9 w-1/3"></div>
    <div class="kb-skeleton h-4 w-1/2"></div>
    <div class="kb-skeleton h-64 w-full"></div>
  </div>

  <!-- ══ No existe ════════════════════════════════════════════════════════════ -->
  <div class="kb-empty" *ngIf="!cargando && noEncontrada">
    <span class="kb-empty-icono"><i class="fas fa-file-alt" aria-hidden="true"></i></span>
    <p class="kb-empty-titulo">No encontramos la solicitud «{{ codigo }}»</p>
    <p class="kb-empty-texto">Puede que el código esté mal escrito, o que sea de otro comercio.</p>
    <button type="button" class="kb-btn kb-btn-outline mt-4" (click)="volver()">
      <i class="fas fa-arrow-left" aria-hidden="true"></i>Volver al listado
    </button>
  </div>

  <!-- ══ Error del servidor — NO es lo mismo que «no existe» ═══════════════════ -->
  <div class="kb-aviso kb-aviso-peligro" *ngIf="!cargando && error">
    <i class="fas fa-exclamation-circle mt-0.5" aria-hidden="true"></i>
    <div>
      <p class="kb-aviso-titulo">No se pudo traer esta solicitud</p>
      <p>{{ error }}</p>
      <div class="mt-3 flex flex-wrap items-center gap-2">
        <button type="button" class="kb-btn kb-btn-outline kb-btn-sm" *ngIf="errorReintentable" (click)="cargar()">
          <i class="fas fa-redo" aria-hidden="true"></i>Volver a consultar la solicitud
        </button>
        <button type="button" class="kb-btn kb-btn-ghost kb-btn-sm" (click)="volver()">
          <i class="fas fa-arrow-left" aria-hidden="true"></i>Volver al listado
        </button>
      </div>
      <!-- Decir «no se resuelve reintentando» y nada más deja al operador sin salida. El
           servidor dijo `retryable:false`, casi siempre porque esta solicitud no es de un
           comercio suyo: se le dice qué hacer con eso. -->
      <p class="kb-hint mt-2" *ngIf="!errorReintentable">
        Esto no se resuelve reintentando. Búscala desde el listado; si tampoco aparece ahí,
        pásale a soporte el código {{ codigo }} y la hora.
      </p>
    </div>
  </div>

  <ng-container *ngIf="!cargando && !noEncontrada && !error && solicitud">

    <!-- ══ 1 · CABECERA — sin rótulo (documento 63 §4.2, bloque 1) ═══════════════
         Código, cuándo, cliente, punto de venta, monto, la pastilla de negocio, y debajo la
         frase de qué pasó — la respuesta al reclamo, antes de cualquier tarjeta. -->
    <div class="kb-ficha">
      <div class="min-w-0 w-full">
        <div class="flex flex-wrap items-center justify-between gap-x-4 gap-y-2">
          <h1 class="kb-ficha-titulo font-mono">{{ solicitud.code || codigo }}</h1>
          <span class="kb-badge" *ngIf="solicitud?.llenado_posterior" title="La solicitud se creó después del crédito, en la caja del modelo anterior">Llenado posterior</span>
          <span class="kb-badge text-sm" [ngClass]="estadoNegocio.clase">{{ estadoNegocio.texto }}</span>
        </div>

        <p class="kb-ficha-meta">
          <span class="tabular-nums">{{ solicitud.created_at | date:'dd/MM/yyyy HH:mm' }}</span>
          <span aria-hidden="true">·</span>
          <span class="break-words">{{ comercio?.name || comercio?.code || 'Comercio sin datos' }}</span>
          <ng-container *ngIf="tienda !== null">
            <span aria-hidden="true">·</span>
            <span>{{ tiendaTexto }}<span *ngIf="caja !== null"> · caja {{ caja }}</span></span>
          </ng-container>
          <!-- El cajero ya llegaba (`cajero_cedula`) y no se pintaba en ninguna parte. Es
               `cashier_declared`: lo que el cajero declaró al cobrar, no una atribución
               probada — y así se dice, en vez de dejarlo pasar por un dato duro. -->
          <ng-container *ngIf="solicitud.cajero_cedula">
            <span aria-hidden="true">·</span>
            <span title="La cédula que el cajero declaró al cobrar. No es una atribución probada.">
              cajero <span class="tabular-nums">{{ cajeroCedula }}</span>
            </span>
          </ng-container>
          <span aria-hidden="true">·</span>
          <span *ngIf="clienteNombre">{{ clienteNombre }}</span>
          <span class="tabular-nums">{{ cedula }}</span>
          <span aria-hidden="true">·</span>
          <span class="font-medium tabular-nums text-foreground">{{ formatoMonto(montoUsd) }}</span>
          <ng-container *ngIf="exchangeRate?.value">
            <span aria-hidden="true">·</span>
            <span class="tabular-nums">tasa {{ formatoNumero(exchangeRate.value) }}<span *ngIf="exchangeRate.date"> ({{ exchangeRate.date | date:'dd/MM/yyyy' }})</span></span>
          </ng-container>
        </p>

        <div class="mt-3 flex flex-wrap items-center gap-2">
          <span class="kb-badge kb-badge-contorno" *ngIf="requestType">
            Tipo de solicitud: {{ textoRequestType(requestType) }}
          </span>
          <span class="kb-badge kb-badge-contorno" *ngIf="vence && vigente">
            <i class="fas fa-hourglass-half" aria-hidden="true"></i>Vence {{ vence | date:'dd/MM/yyyy HH:mm' }}
          </span>
        </div>

        <!-- 🔴 LA FRASE QUE CONTESTA EL RECLAMO (documento 63 §1.2). Es lo mejor que tiene
             esta pantalla: se calcula con el dato exacto de cada caso y no se toca. -->
        <p class="mt-4 text-sm text-foreground max-w-3xl" *ngIf="fraseQuePaso">{{ fraseQuePaso }}</p>
      </div>
    </div>

    <!-- ══ 2 · HISTORIAL — línea de tiempo, pieza central de esta pantalla ════════
         🔴 documento 63 §4.2: absorbe el carril del cobro y la tabla de intentos (los dos
         tienen `at`) y enriquece el hito ANULADA con quién autorizó — ya no hay tarjetas
         propias «Intentos de cobro» ni «Anulación». -->
    <section class="kb-tarjeta">
      <div class="kb-tarjeta-cab">
        <h2 class="text-base font-semibold leading-none">Historial</h2>
        <span class="kb-tarjeta-accion shrink-0 text-xs text-muted-foreground">De más viejo a más nuevo</span>
      </div>
      <div class="kb-tarjeta-cuerpo">

      <!-- ── El carril del cobro — el único dibujo de esta pantalla (documento 64 §3.1) ── -->
      <div class="pb-6 border-b border-border" *ngIf="carril as carril">
        <h3 class="text-sm font-semibold text-foreground mb-2">Cobro de la inicial</h3>

        <ng-container [ngSwitch]="carril.estado">

          <!-- No sabemos: el servidor no mandó `cobro_attempts`. Un carril rayado, no uno vacío. -->
          <div *ngSwitchCase="'sin_dato'">
            <svg viewBox="0 0 600 16" class="w-full" style="height:16px" role="img"
                 aria-label="Sin dato: no se pudo saber si hubo intentos de cobro parcial.">
              <defs>
                <pattern id="carrilCobroRayado" width="8" height="8" patternTransform="rotate(45)" patternUnits="userSpaceOnUse">
                  <line x1="0" y1="0" x2="0" y2="8" stroke="hsl(var(--border))" stroke-width="4"></line>
                </pattern>
              </defs>
              <rect x="0" y="0" width="500" height="16" fill="url(#carrilCobroRayado)"></rect>
            </svg>
            <p class="kb-hint mt-1.5">No se pudo saber si hubo intentos de cobro parcial: el servidor no mandó ese dato.</p>
          </div>

          <!-- Hubo cero intentos, y SE SABE que fueron cero: no es lo mismo que «no sabemos». -->
          <p class="kb-empty-texto" *ngSwitchCase="'sin_intentos'">{{ carril.mensaje }}</p>

          <!-- Hubo cobro, pero no hay con qué comparar la longitud (caso raro, sin objetivo). -->
          <p class="kb-empty-texto" *ngSwitchCase="'sin_objetivo'">
            Se cobró {{ formatoMonto(carril.cobrado) }} en {{ carril.intentos }} intento{{ carril.intentos === 1 ? '' : 's' }},
            pero no hay un objetivo con qué compararlo.
          </p>

          <!-- El carril con datos: longitud cobrada contra la marca de referencia (el objetivo). -->
          <div *ngSwitchCase="'con_datos'">
            <svg viewBox="0 0 600 16" class="w-full" style="height:16px" role="img" [attr.aria-label]="carril.ariaLabel">
              <rect x="0" y="0" width="500" height="16" fill="hsl(var(--muted))"></rect>
              <rect *ngFor="let s of carril.segmentos"
                    [attr.x]="s.x" y="0" [attr.width]="s.ancho" height="16" fill="var(--dd-brand)">
                <title>{{ s.titulo }}</title>
              </rect>
              <rect *ngIf="carril.excedente" [attr.x]="carril.excedente.x" y="0"
                    [attr.width]="carril.excedente.ancho" height="16" fill="var(--dd-warn)">
                <title>Sobran {{ formatoMonto(carril.diff) }}</title>
              </rect>
              <rect x="499" y="0" width="2" height="16" fill="hsl(var(--foreground))">
                <title>Objetivo {{ formatoMonto(carril.objetivo) }}</title>
              </rect>
            </svg>
            <p class="mt-1.5 text-xs tabular-nums text-muted-foreground">
              Cobrado {{ formatoMonto(carril.cobrado) }} de {{ formatoMonto(carril.objetivo) }} ·
              <ng-container *ngIf="carril.diff > 0.004">{{ carril.sobra ? 'sobran' : 'faltan' }} {{ formatoMonto(carril.diff) }}</ng-container>
              <ng-container *ngIf="carril.diff <= 0.004">exacto</ng-container>
            </p>

            <!-- Sin tooltip propio: la tabla de intentos es el detalle completo (documento 64 §3.1). -->
            <div class="kb-tabla-plana mt-3">
              <table class="kb-table">
                <thead class="kb-thead">
                  <tr>
                    <th class="kb-th">Cuándo</th>
                    <th class="kb-th">Cajero</th>
                    <th class="kb-th">Resultado</th>
                    <th class="kb-th-num">Monto cobrado</th>
                  </tr>
                </thead>
                <tbody>
                  <tr class="kb-tr last:border-b-0" *ngFor="let a of cobroAttempts">
                    <td class="kb-td tabular-nums">{{ a.at | date:'dd/MM/yyyy HH:mm' }}</td>
                    <td class="kb-td kb-td-mono">{{ a.cashier || '—' }}</td>
                    <td class="kb-td">
                      <span class="kb-badge" [ngClass]="claseResultadoCobro(a.result)">{{ textoResultadoCobro(a.result) }}</span>
                    </td>
                    <td class="kb-td kb-td-num">{{ formatoMonto(a.amount_usd) }}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </ng-container>
      </div>

      <!-- ── La línea de tiempo ── -->
      <div class="kb-empty" *ngIf="!historial.length">
        <span class="kb-empty-icono"><i class="fas fa-route" aria-hidden="true"></i></span>
        <p class="kb-empty-titulo">Todavía no hay movimientos registrados</p>
        <p class="kb-empty-texto">Esta solicitud no trae historial en la respuesta del servidor.</p>
      </div>

      <ol class="relative space-y-6 border-l-2 border-border pl-6" *ngIf="historial.length">
        <li class="relative" *ngFor="let h of historial; let i = index; let ultimo = last">
          <p class="mb-1.5 text-xs tabular-nums text-muted-foreground" *ngIf="huecoAntes(i) as hueco">{{ hueco }}</p>

          <span class="absolute -left-[29px] top-0.5 h-4 w-4 rounded-full ring-4 ring-background"
                [ngClass]="colorHito(h)"
                [class.ring-2]="ultimo && esTerminal" [class.ring-primary]="ultimo && esTerminal"></span>

          <div class="flex flex-wrap items-baseline gap-x-2">
            <span class="text-sm font-semibold text-foreground">{{ tituloHito(h) }}</span>
            <span class="kb-badge kb-badge-marca" *ngIf="ultimo && esTerminal">Estado actual</span>
            <span class="text-xs tabular-nums text-muted-foreground" *ngIf="fechaHito(h)">
              {{ fechaHito(h) | date:'dd/MM/yyyy HH:mm' }}
            </span>
          </div>
          <p class="mt-4 text-sm text-muted-foreground" *ngIf="detalleParaHito(h)">{{ detalleParaHito(h) }}</p>
          <p class="mt-4 text-xs text-muted-foreground" *ngIf="actorHito(h)">{{ actorHito(h) }}</p>

          <!-- Las cuatro escrituras del cobro, una sola vez, en el hito que corresponde. -->
          <div class="mt-4 flex flex-wrap gap-1.5" *ngIf="pasosHito(h) as pasos">
            <span class="kb-badge" *ngFor="let p of pasos"
                  [ngClass]="p.ok === true ? 'kb-badge-exito' : (p.ok === false ? 'kb-badge-peligro' : 'kb-badge-neutro')">
              <i class="fas" [ngClass]="p.ok === true ? 'fa-check' : (p.ok === false ? 'fa-times' : 'fa-question')" aria-hidden="true"></i>
              {{ p.ok === null ? p.texto + ' — sin comprobar' : p.texto }}
            </span>
          </div>
        </li>

        <!-- El nodo hueco: sigue viva, y hace cuánto que no se mueve. -->
        <li class="relative" *ngIf="huecoFinal as hueco">
          <p class="mb-1.5 text-xs tabular-nums text-muted-foreground">{{ hueco }}</p>
          <span class="absolute -left-[29px] top-0.5 h-4 w-4 rounded-full ring-4 ring-background border-2 border-dashed border-muted-foreground bg-background"></span>
          <span class="text-sm italic text-muted-foreground">Sigue aquí — todavía no termina</span>
        </li>
      </ol>
      </div>
    </section>

    <!-- ══ 3 · PLAN DE PAGO — hoy no existía (documento 64 §3.2) ══════════════════ -->
    <section class="kb-tarjeta">
      <div class="kb-tarjeta-cab">
        <h2 class="text-base font-semibold leading-none">Plan de pago</h2>
      </div>
      <div class="kb-tarjeta-cuerpo">

      <div class="kb-empty" *ngIf="!plan">
        <span class="kb-empty-icono"><i class="fas fa-file-invoice-dollar" aria-hidden="true"></i></span>
        <p class="kb-empty-titulo">Todavía no hay un plan asignado</p>
        <p class="kb-empty-texto" *ngIf="textoClosedReason(closedReason) as motivo">{{ motivo }}</p>
      </div>

      <ng-container *ngIf="plan">
        <div>
          <p class="text-sm tabular-nums text-foreground">{{ planResumen }}</p>
          <p class="kb-hint" *ngIf="plan.name">{{ plan.name }}</p>
        </div>

        <dl class="kb-datos">
          <dt class="kb-dato-et">Factura</dt>
          <dd class="kb-dato-val m-0">{{ factura }}</dd>

          <ng-container *ngIf="solicitud.initial_payment?.merchant_order as orden">
            <dt class="kb-dato-et">Orden del comercio</dt>
            <dd class="kb-dato-val m-0 font-mono">{{ orden }}</dd>
          </ng-container>

          <ng-container *ngIf="solicitud.merchant_ref as ref">
            <dt class="kb-dato-et">Referencia del comercio</dt>
            <dd class="kb-dato-val m-0 font-mono">{{ ref }}</dd>
          </ng-container>
        </dl>

        <div class="kb-tabla-plana" *ngIf="installmentsConEstado.length">
          <table class="kb-table">
            <thead class="kb-thead">
              <tr>
                <th class="kb-th">#</th>
                <th class="kb-th">Vence</th>
                <th class="kb-th-num">Monto</th>
                <th class="kb-th">Estado</th>
              </tr>
            </thead>
            <tbody>
              <tr class="kb-tr last:border-b-0" *ngFor="let c of installmentsConEstado">
                <td class="kb-td tabular-nums">{{ c.order }}</td>
                <td class="kb-td tabular-nums">{{ c.scheduled_date ? (c.scheduled_date | date:'dd/MM/yyyy') : '—' }}</td>
                <td class="kb-td kb-td-num">{{ formatoMonto(c.amount) }}</td>
                <td class="kb-td"><span class="kb-badge" [ngClass]="c.estadoClase">{{ c.estadoTexto }}</span></td>
              </tr>
            </tbody>
          </table>
        </div>
      </ng-container>
      </div>
    </section>

    <!-- ══ 4 · CRÉDITO — el que nació, o por qué no (documento 63 §4.2, bloque 4) ══ -->
    <section class="kb-tarjeta">
      <div class="kb-tarjeta-cab">
        <h2 class="text-base font-semibold leading-none">Crédito</h2>
      </div>
      <div class="kb-tarjeta-cuerpo">

      <div *ngIf="creditoNumero">
        <div class="flex flex-wrap items-center gap-3">
          <span class="kb-badge kb-badge-marca text-base font-mono px-3 py-1">{{ creditoNumero }}</span>
          <button type="button" class="kb-btn kb-btn-ghost kb-btn-sm" (click)="copiarCredito()">
            <i class="fas" [ngClass]="copiado ? 'fa-check' : 'fa-copy'" aria-hidden="true"></i>
            {{ copiado ? 'Copiado' : 'Copiar número' }}
          </button>
        </div>
        <!-- ⚠️ Sin enlace a propósito: no hay hoy una pantalla que resuelva este número. -->
        <p class="kb-hint mt-1.5">Sin enlace: hoy no hay una pantalla que resuelva este número.</p>
        <!-- Liquidación al comercio de este crédito (EST-01/02; maqueta escenario 4). Solo con liquidacion.consultar. -->
        <app-liquidacion-marca-venta [code]="creditoNumero" class="mt-4 block"></app-liquidacion-marca-venta>
      </div>

      <div class="kb-empty" *ngIf="!creditoNumero">
        <span class="kb-empty-icono"><i class="fas fa-ban" aria-hidden="true"></i></span>
        <p class="kb-empty-titulo">Esta solicitud no llegó a tener un crédito</p>
        <p class="kb-empty-texto" *ngIf="etapa">{{ textoEtapa(etapa) }}</p>
      </div>

      <!-- Dos campos curados de `credito_real` — el estado REAL, leído del crédito, no una
           copia de la solicitud. Se fue «Código del estado»: era el código interno al lado
           del nombre que ya está en el renglón de arriba. -->
      <dl class="kb-datos" *ngIf="creditoReal">
        <dt class="kb-dato-et">Activo</dt>
        <dd class="m-0">
          <span class="kb-dato-val" *ngIf="creditoRealActivo !== null">{{ creditoRealActivo ? 'Sí' : 'No' }}</span>
          <span class="kb-dato-nota" *ngIf="creditoRealActivo === null">Sin dato</span>
        </dd>

        <dt class="kb-dato-et">Estado del pago</dt>
        <dd class="m-0">
          <span class="kb-dato-val" *ngIf="creditoRealEstadoPago">{{ creditoRealEstadoPago.name | titlecase | slice:0:1 }}{{ (creditoRealEstadoPago.name || '').slice(1).toLowerCase() }}</span>
          <span class="kb-dato-nota" *ngIf="!creditoRealEstadoPago">Sin dato</span>
        </dd>
      </dl>
      </div>
    </section>

  </ng-container>
</div>
